import clsx from 'clsx';
import { forwardRef } from 'react';

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
	children: React.ReactNode;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
	({ className, children, ...props }, ref) => (
		<div className={clsx('relative w-full', className)}>
			<table
				ref={ref}
				tabIndex={0}
				className={clsx('text-baseline-950 w-full text-sm')}
				{...props}
			>
				{children}
			</table>
		</div>
	)
);
Table.displayName = 'Table';

interface TableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {
	children: React.ReactNode;
}

export const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
	({ className, children, ...props }, ref) => (
		<thead
			ref={ref}
			className={clsx('h-[40px] [&_tr]:border-b [&_tr]:border-r [&_tr]:border-l', className)}
			{...props}
		>
			{children}
		</thead>
	)
);
TableHeader.displayName = 'TableHeader';

interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
	children: React.ReactNode;
}

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
	({ className, children, ...props }, ref) => (
		<tbody
			ref={ref}
			className={clsx(
				'[&_tr]:border-b-0 [&_tr:last-child]:border-b [&_tr]:border-l [&_tr]:border-r border-gray-300',
				className
			)}
			{...props}
		>
			{children}
		</tbody>
	)
);
TableBody.displayName = 'TableBody';

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
	children: React.ReactNode;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
	({ className, children, ...props }, ref) => (
		<tr
			ref={ref}
			className={clsx(
				'hover:bg-blue-50 even:bg-white border odd:bg-gray-100 border-gray-300 border-l-0 border-r-0 border-t',
				className
			)}
			{...props}
		>
			{children}
		</tr>
	)
);
TableRow.displayName = 'TableRow';

interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
	children: React.ReactNode;
}

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
	({ className, children, ...props }, ref) => (
		<th
			ref={ref}
			className={clsx(
				'h-[40px] border-r text-muted-foreground bg-white px-2 text-left align-middle font-semibold [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] border-gray-300',
				className
			)}
			{...props}
		>
			{children}
		</th>
	)
);
TableHead.displayName = 'TableHead';

interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
	children: React.ReactNode;
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
	({ className, children, ...props }, ref) => (
		<td
			ref={ref}
			className={clsx(
				'h-[40px] border-r px-2 align-middle text-base [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] border-gray-300',
				className
			)}
			{...props}
		>
			{children}
		</td>
	)
);
TableCell.displayName = 'TableCell';
